import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import css from './AIInsightPanel.module.scss';

// AI 推荐抽屉 —— 顶部贴右侧，可折叠
// 顶部一个 LOGO + 折叠按钮，下面分 5 个 section（对应 5 个青铜功能）
// 总开关关闭时整个抽屉收起隐藏；开启后默认展开
// 触摸设备（iPad 等）默认收起，避免遮挡英雄网格

const STORAGE_KEY = 'lol_coach_ai_drawer_collapsed_v1';

const getInitialCollapsed = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === '1';
  } catch {}
  // 没有用户偏好时：触摸设备默认收起；桌面默认展开
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(pointer: coarse)').matches;
  }
  return false;
};

const AIInsightPanel = ({ insights, aiSettings }) => {
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  if (!aiSettings.isAIOn) return null;
  if (!insights) return null;

  const hasAny =
    insights.priority ||
    insights.gapScan ||
    insights.counter ||
    insights.composition ||
    insights.opAlerts;

  // ===== 折叠态：整根窄条都是可点击的"展开把手" =====
  if (collapsed) {
    return (
      <button
        type="button"
        className={css.CollapsedHandle}
        onClick={() => setCollapsed(false)}
        aria-label="展开 AI 推荐抽屉"
        title="展开 AI 推荐"
      >
        <span className={css.HandleDot} />
        <span className={css.HandleText}>
          {'AI COACH'.split('').map((ch, i) => (
            <span key={i}>{ch === ' ' ? '·' : ch}</span>
          ))}
        </span>
        <span className={css.HandleArrow}>‹</span>
      </button>
    );
  }

  return (
    <div className={css.Drawer}>
      <div className={css.Header}>
        <div className={css.HeaderLeft}>
          <span className={css.Dot} />
          <span className={css.Brand}>AI COACH</span>
          <span className={css.Tier}>青铜</span>
        </div>
        <button
          type="button"
          className={css.CollapseBtn}
          onClick={() => setCollapsed(true)}
          aria-label="折叠"
          title="折叠到右侧"
        >
          ›
        </button>
      </div>

      <div className={css.Body}>
        {!hasAny && (
          <div className={css.Empty}>
            开始 BP 后将自动出现推荐与分析
          </div>
        )}

        {insights.priority && <PrioritySection data={insights.priority} />}
        {insights.counter && <CounterSection data={insights.counter} />}
        {insights.opAlerts && <OpAlertsSection data={insights.opAlerts} />}
        {insights.composition && <CompScoreSection data={insights.composition} />}
        {insights.gapScan && <GapScanSection data={insights.gapScan} />}
      </div>
    </div>
  );
};

// ============ 功能 1：优先级提示 ============
const PrioritySection = ({ data }) => {
  const action = data.type === 'ban' ? 'Ban' : 'Pick';
  const teamCN = data.team === 'blue' ? '蓝方' : '红方';
  return (
    <Section
      title={`本回合推荐 ${action}`}
      subtitle={`${teamCN} 第 ${data.slot + 1} 手 · ${data.reasoning}`}
      accent={data.type === 'ban' ? 'red' : 'blue'}
    >
      <div className={css.CardList}>
        {data.candidates.map((c, i) => (
          <ChampionMiniCard
            key={c.champion.id}
            rank={i + 1}
            champion={c.champion}
            stat={c.stat}
            score={c.score}
          />
        ))}
      </div>
    </Section>
  );
};

// ============ 功能 3：Counter Pick ============
const CounterSection = ({ data }) => (
  <Section
    title={`针对 ${data.against.name} 推荐反制`}
    subtitle="基于伤害类型 / 控制 / 强势期等维度匹配"
    accent="gold"
  >
    <div className={css.CardList}>
      {data.candidates.map((c, i) => (
        <ChampionMiniCard
          key={c.champion.id}
          rank={i + 1}
          champion={c.champion}
          reasons={c.reasons}
        />
      ))}
    </div>
  </Section>
);

// ============ 功能 5：OP 警告 ============
const OpAlertsSection = ({ data }) => {
  if (data.alive.length === 0) return null;
  return (
    <Section
      title={`版本 OP 仍在场上 · ${data.alive.length} 名`}
      subtitle="P+B 总和最高的英雄尚未 ban/pick"
      accent="red"
    >
      <div className={css.OpGrid}>
        {data.alive.map((x) => (
          <div key={x.champion.id} className={css.OpItem}>
            <img
              src={x.champion.squareImg}
              alt={x.champion.name}
              className={css.OpImg}
              crossOrigin="anonymous"
            />
            <div className={css.OpInfo}>
              <div className={css.OpName}>{x.champion.name}</div>
              <div className={css.OpScore}>
                P{Math.round(x.stat.pick_rate_pct || 0)}
                <span className={css.OpSep}>·</span>
                B{Math.round(x.stat.ban_rate_pct || 0)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

// ============ 功能 4：阵容评分 ============
const CompScoreSection = ({ data }) => {
  const { blue, red } = data;
  if (blue.total === 0 && red.total === 0) return null;
  return (
    <Section title="阵容综合评分" subtitle="早 / 中 / 后期阶段实力对比" accent="gold">
      <div className={css.ScoreGrid}>
        <ScoreColumn label="蓝方" team="blue" score={blue} />
        <ScoreColumn label="红方" team="red"  score={red} />
      </div>
    </Section>
  );
};

const ScoreColumn = ({ label, team, score }) => (
  <div className={cx(css.ScoreCol, css[`ScoreCol_${team}`])}>
    <div className={css.ScoreColLabel}>{label}</div>
    <ScoreBar phase="早期" value={score.early} team={team} />
    <ScoreBar phase="中期" value={score.mid}   team={team} />
    <ScoreBar phase="后期" value={score.late}  team={team} />
    <div className={css.ScoreTotal}>
      综合 <strong>{score.total.toFixed(1)}</strong>
    </div>
  </div>
);

const ScoreBar = ({ phase, value, team }) => (
  <div className={css.ScoreRow}>
    <span className={css.ScorePhase}>{phase}</span>
    <span className={css.ScoreTrack}>
      <span
        className={cx(css.ScoreFill, css[`ScoreFill_${team}`])}
        style={{ width: `${Math.max(0, Math.min(100, value * 10))}%` }}
      />
    </span>
    <span className={css.ScoreValue}>{value.toFixed(1)}</span>
  </div>
);

// ============ 功能 2：位置漏洞扫描 ============
const GapScanSection = ({ data }) => {
  const blueIssues = (data.blue && data.blue.issues) || [];
  const redIssues = (data.red && data.red.issues) || [];
  const blueCompl = (data.blue && data.blue.compliments) || [];
  const redCompl = (data.red && data.red.compliments) || [];
  if (blueIssues.length === 0 && redIssues.length === 0 && blueCompl.length === 0 && redCompl.length === 0) {
    return null;
  }
  return (
    <Section title="阵容结构扫描" subtitle="伤害类型 / 前排 / 控制 / 强势期" accent="blue">
      <div className={css.GapGrid}>
        <GapColumn label="蓝方" team="blue" issues={blueIssues} compliments={blueCompl} />
        <GapColumn label="红方" team="red"  issues={redIssues}  compliments={redCompl} />
      </div>
    </Section>
  );
};

const GapColumn = ({ label, team, issues, compliments }) => (
  <div className={cx(css.GapCol, css[`GapCol_${team}`])}>
    <div className={css.GapColLabel}>{label}</div>
    {compliments.map((c, i) => (
      <div key={`c-${i}`} className={cx(css.GapItem, css.GapItemGood)}>✓ {c}</div>
    ))}
    {issues.map((it, i) => (
      <div key={`i-${i}`} className={cx(css.GapItem, css[`GapItem_${it.level}`])}>
        {it.level === 'warn' ? '⚠' : 'ⓘ'} {it.text}
      </div>
    ))}
    {issues.length === 0 && compliments.length === 0 && (
      <div className={css.GapItemEmpty}>—</div>
    )}
  </div>
);

// ============ 通用组件 ============
const Section = ({ title, subtitle, accent, children }) => (
  <div className={cx(css.Section, css[`SectionAccent_${accent}`])}>
    <div className={css.SectionHeader}>
      <div className={css.SectionTitle}>{title}</div>
      <div className={css.SectionSubtitle}>{subtitle}</div>
    </div>
    {children}
  </div>
);

const ChampionMiniCard = ({ rank, champion, stat, score, reasons }) => (
  <div className={css.MiniCard}>
    <span className={css.MiniRank}>{rank}</span>
    <img
      src={champion.squareImg}
      alt={champion.name}
      className={css.MiniImg}
      crossOrigin="anonymous"
    />
    <div className={css.MiniBody}>
      <div className={css.MiniName}>{champion.name}</div>
      {stat && (
        <div className={css.MiniStats}>
          P{Math.round(stat.pick_rate_pct || 0)} ·
          B{Math.round(stat.ban_rate_pct || 0)} ·
          W{Math.round(stat.win_rate_pct || 0)}%
        </div>
      )}
      {reasons && reasons.length > 0 && (
        <div className={css.MiniReasons}>
          {reasons.slice(0, 2).map((r, i) => (
            <span key={i} className={css.ReasonChip}>{r}</span>
          ))}
        </div>
      )}
    </div>
    {typeof score === 'number' && (
      <div className={css.MiniScore}>{Math.round(score)}</div>
    )}
  </div>
);

export default AIInsightPanel;
